import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';

const AgreementAcceptPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [sections, setSections] = useState([]);
  const [variables, setVariables] = useState({});
  const [accepted, setAccepted] = useState(false);

  const loadDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!token) {
        throw new Error('Acceptance token missing. Please use the link provided in your email.');
      }
      const { data, error } = await supabase.rpc('agreement_get_token_details', {
        p_token: token
      });
      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error || 'Unable to validate token.');
      }
      if (data?.agreement?.id !== id) {
        throw new Error('Token does not match this agreement.');
      }
      if (data?.token?.used_at) {
        setAccepted(true);
      }
      setDetails(data);

      const { agreement } = data;
      if (agreement?.id) {
        const [{ data: itemRows }, { data: invoiceRows }, { data: paymentRows }, { data: latestVersionRows }] = await Promise.all([
          supabase
            .from('agreement_items')
            .select('id, item_type, title, quantity, unit_price, subtotal')
            .eq('agreement_id', agreement.id)
            .order('sort_order'),
          supabase
            .from('invoices')
            .select('id, amount_due, status, due_date, updated_at')
            .eq('agreement_id', agreement.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('payments')
            .select('id, amount, method, reference, paid_at, updated_at')
            .eq('agreement_id', agreement.id)
            .order('paid_at', { ascending: false }),
          supabase
            .from('agreement_versions')
            .select('sections, variables')
            .eq('agreement_id', agreement.id)
            .order('version_number', { ascending: false })
            .limit(1)
        ]);
        setItems(itemRows || []);
        setInvoices(invoiceRows || []);
        setPayments(paymentRows || []);
        if (latestVersionRows?.length) {
          const latest = latestVersionRows[0];
          setSections(Array.isArray(latest.sections) ? latest.sections : []);
          setVariables(latest.variables || {});
        } else {
          setSections([]);
          setVariables({});
        }
      }
    } catch (err) {
      console.error('Agreement token error', err);
      setError(err.message || 'Unable to load agreement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const handleAccept = async () => {
    try {
      setAccepting(true);
      const metadata = {
        accepted_via: 'web_portal',
        accepted_at: new Date().toISOString()
      };
      const { data, error } = await supabase.rpc('agreement_accept_with_token', {
        p_token: token,
        p_signature_type: 'click',
        p_signature_data: null,
        p_metadata: metadata
      });
      if (error) throw error;
      if (!data?.success) {
        throw new Error(data?.error || 'Acceptance failed.');
      }
      const recipientId = data?.recipient_id || null;
      setAccepted(true);
      const query = recipientId ? `?recipient=${recipientId}` : '';
      navigate(`/agreements/${id}/signed${query}`, { replace: true });
    } catch (err) {
      console.error('Agreement accept error', err);
      setError(err.message || 'Unable to accept agreement.');
    } finally {
      setAccepting(false);
    }
  };

  const agreement = details?.agreement;
  const recipient = details?.recipient;
  const tokenInfo = details?.token;

  const compiledSections = useMemo(() => {
    return (sections || []).map((section) => {
      let content = section.content || '';
      Object.entries(variables || {}).forEach(([key, value]) => {
        const pattern = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        content = content.replace(pattern, value ?? '');
      });
      return {
        ...section,
        content
      };
    });
  }, [sections, variables]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    const numeric = Number(amount);
    if (Number.isNaN(numeric)) return String(amount);
    const base = numeric.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return agreement?.currency ? `${base} ${agreement.currency}` : base;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <p className="mt-4 text-sm text-gray-600">Loading agreement…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <div className="max-w-md bg-white border border-red-200 text-red-700 px-6 py-4 rounded shadow-sm">
          <h1 className="text-lg font-semibold mb-2">Unable to open agreement</h1>
          <p className="text-sm">{error}</p>
          <p className="text-xs text-gray-500 mt-3">If this issue persists, please contact your Ravana Migration advisor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-6 py-10">
      <div className="max-w-2xl w-full bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-gray-900">
            {agreement?.title || 'Agreement'}
          </h1>
          <p className="text-sm text-gray-600">
            Presented by <span className="font-medium text-gray-800">Ravana Migration</span>
          </p>
        </header>

        <section className="space-y-1 text-sm text-gray-700">
          <div>
            <span className="font-medium text-gray-900">Recipient:</span>{' '}
            {recipient?.full_name} ({recipient?.email})
          </div>
          <div>
            <span className="font-medium text-gray-900">Agreement ID:</span> {agreement?.id}
          </div>
          <div>
            <span className="font-medium text-gray-900">Status:</span>{' '}
            {accepted ? 'Signed' : recipient?.status || 'pending'}
          </div>
          <div>
            <span className="font-medium text-gray-900">Value:</span>{' '}
            {agreement?.total_amount} {agreement?.currency}
          </div>
          {tokenInfo?.expires_at && (
            <div className="text-xs text-gray-500">
              Link expires at {new Date(tokenInfo.expires_at).toLocaleString()}
            </div>
          )}
        </section>

        <section className="space-y-3 text-sm text-gray-700">
          <p>
            <strong>Next steps:</strong> please review the agreement that was shared with you by your advisor.
            When you are ready to accept, click the button below to record your acceptance. Your acceptance is logged
            with a timestamp and device information.
          </p>
          <p>
            If you have questions or need modifications, reach out to your advisor before accepting. You will receive
            a signed copy for your records once all parties have completed the process.
          </p>
        </section>

        <section className="bg-gray-50 border border-gray-200 rounded px-4 py-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-900">Agreement Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-700">
            <div>
              <div className="font-medium text-gray-900 uppercase tracking-wide text-[10px]">Total amount</div>
              <div>{formatCurrency(agreement?.total_amount)}</div>
            </div>
            <div>
              <div className="font-medium text-gray-900 uppercase tracking-wide text-[10px]">Last updated</div>
              <div>{agreement?.updated_at ? new Date(agreement.updated_at).toLocaleString() : '-'}</div>
            </div>
          </div>

          <div className="space-y-1">
            <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Items</h3>
            {items.length === 0 ? (
              <p className="text-xs text-gray-500">No items listed in this agreement.</p>
            ) : (
              <ul className="space-y-1">
                {items.map((item) => (
                  <li key={item.id} className="flex justify-between text-xs text-gray-700">
                    <span>
                      {item.title || item.item_type} ({item.quantity} × {formatCurrency(item.unit_price)})
                    </span>
                    <span className="font-medium text-gray-900">{formatCurrency(item.subtotal)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-1">
            <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Invoices</h3>
            {invoices.length === 0 ? (
              <p className="text-xs text-gray-500">No invoices have been issued yet.</p>
            ) : (
              <ul className="space-y-1">
                {invoices.map((invoice) => (
                  <li key={invoice.id} className="flex justify-between text-xs text-gray-700">
                    <span>
                      {formatCurrency(invoice.amount_due)} • Due {invoice.due_date || '-'}
                    </span>
                    <span className="uppercase text-[10px] text-gray-500">{invoice.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-1">
            <h3 className="text-xs font-semibold text-gray-800 uppercase tracking-wide">Payments received</h3>
            {payments.length === 0 ? (
              <p className="text-xs text-gray-500">No payments recorded yet.</p>
            ) : (
              <ul className="space-y-1">
                {payments.map((payment) => (
                  <li key={payment.id} className="flex justify-between text-xs text-gray-700">
                    <span>
                      {formatCurrency(payment.amount)} • {payment.method || '—'}
                    </span>
                    <span className="text-[10px] text-gray-500">
                      {payment.paid_at ? new Date(payment.paid_at).toLocaleString() : '-'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="space-y-4 text-sm text-gray-700">
          <h2 className="text-md font-semibold text-gray-900">Agreement Content</h2>
          {compiledSections.length === 0 ? (
            <p className="text-sm text-gray-500">Agreement content is not available. Please refer to the PDF shared by your advisor.</p>
          ) : (
            compiledSections.map((section) => (
              <article key={section.id || section.position} className="space-y-2 bg-white border border-gray-200 rounded px-4 py-3">
                {section.title && (
                  <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                )}
                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {section.content}
                </div>
              </article>
            ))
          )}
        </section>

        {!accepted ? (
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="w-full px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded shadow-sm text-sm font-medium disabled:opacity-60"
          >
            {accepting ? 'Recording acceptance…' : 'I Accept the Agreement'}
          </button>
        ) : (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">
            Thank you! Your acceptance has been recorded. A confirmation email will be sent by your advisor shortly.
          </div>
        )}

        <footer className="text-xs text-gray-500 text-center">
          Need assistance? Contact{' '}
          <a href="mailto:agreements@ravana.com" className="text-blue-600 hover:underline">
            agreements@ravana.com
          </a>{' '}
          or return to{' '}
          <Link to="/" className="text-blue-600 hover:underline">
            Ravana Migration
          </Link>
          .
        </footer>
      </div>
    </div>
  );
};

export default AgreementAcceptPage;
